import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {NotificationsOutlined, NotificationsRounded, WarningRounded} from "@mui/icons-material";
import {Badge, Button, CircularProgress, Popover, SvgIcon, Tooltip, useMediaQuery} from "@mui/material";
import debounce from "lodash/debounce";
import useIsMounted from "../../../../hooks/use-is-mounted";
import Api from "../../../../../core/services/api/api";
import FirebaseNotificationService from "../../../../../core/services/firebase";
import ToastService from "../../../../../core/services/toast";
import IconButton from "../../../base/button/icon_button";
import InfiniteScroll from "react-infinite-scroller";
import {CSSTransition, TransitionGroup} from "react-transition-group";
import NotificationCard from "../../../app-specific/notification-cards/notification-card";
import {Col} from "reactstrap";
import {useHistory} from "react-router-dom";
import {routes} from "../../../../../core/models/static/routes/app-routes";
import {ReactComponent as AccessDenied} from "../../../../../assets/images/art-like/access_denied.svg";
import {ReactComponent as SettingsIcon} from "../../../../../assets/images/notifications/settings.svg";
import {ReactComponent as NoNotifications} from "../../../../../assets/images/art-like/no-notifications.svg";
import useAccessLevels from "../../../../hooks/use-access-levels";
import {useTranslation} from "react-i18next";

const useNotificationsEndAction = (breakpoint) => {
    const [popoverAnchor, setPopoverAnchor] = useState();
    const passedBreakpoint = useMediaQuery((theme) => theme.breakpoints.up(breakpoint));
    const popoverAction = useRef(null);
    const [unreadCount, setUnreadCount] = useState(0);
    const [hasAccess, setHasAccess] = useState(true);
    const isMounted = useIsMounted();
    const operatorAccessLevels = useAccessLevels();
    const {t} = useTranslation("components", {keyPrefix: 'panel-sections.app-bar.actions.notifications'});


    /**
     * Open the plugins popover
     */
    const openPopover = useCallback((e = undefined, anchorElement = undefined) => {
        setPopoverAnchor(anchorElement ?? e?.currentTarget ?? document.getElementById('panel-notifications-popover-button'));
    }, [])

    /**
     * Close the plugins popover
     */
    const closePopover = useCallback(() => {
        setPopoverAnchor(undefined)
    }, [])

    /**
     * Handles the api errors when dealing with Biz-live  system.
     *
     * in both cases, removes the access of this feature of the system.
     * @param {ApiResponseDs<null>} response
     */
    const unAuthorizedOrAccessDeniedCallback = useCallback((response) => {
        switch (response?.errorCode) {
            case 401:
            case 403:
            case -401:
            case -403:
                setHasAccess(false);
                setPopoverAnchor(null)
                break;
            default:
                break;
        }
    }, [])

    /**
     * Checks whether the user has given notification permission.
     * @type {boolean}
     */
    const hasNotificationPermission = useMemo(() => {
        if (!operatorAccessLevels.packages.notifications.viewList)
            return false;
        if (!FirebaseNotificationService.isSupported())
            return false;
        return Notification.permission !== "denied";
    }, [operatorAccessLevels.packages.notifications.viewList])

    /**
     * Fetches the unread-count of the notifications.
     * @type {(function(): Promise<void>)}
     */
    const getUnreadCount = useCallback(async () => {
        if (!operatorAccessLevels.packages.notifications.viewList) {
            const response = await Api.getUnreadNotificationCount(unAuthorizedOrAccessDeniedCallback);
            if (!isMounted())
                return
            setUnreadCount(response?.data ?? 0)
        }

    }, [isMounted, unAuthorizedOrAccessDeniedCallback, operatorAccessLevels.packages.notifications.viewList])

    /**
     * Fetches the notifications of this user from the server provided the current pagination info.
     *
     * this method is called with a pagination info of pageSize 1 and currentPage 1 if and only if a new item has
     * been received from the notifications.
     * @param {any} paginationInfo the paginationInfo of the search.
     * @return {Promise<*[]>}
     */
    const getNotifications = useCallback(async (paginationInfo) => {
        const forApi = {
            pageNumber: paginationInfo.currentPage,
            itemCounts: paginationInfo.pageSize,
        }
        return await Api.getNotifications(forApi, unAuthorizedOrAccessDeniedCallback);
    }, [unAuthorizedOrAccessDeniedCallback])

    /**
     * Triggers every time a new message is received while the application is in the foreground.
     *
     * Show manual notification toast in case of a in-app messaging notification provocation
     * @param {any} _
     */
    const onInAppNotificationReceived = useCallback(async (_) => {
        setUnreadCount(prevState => prevState + 1)
        const response = await getNotifications({pageSize: 1, currentPage: 1});
        const notifications = response?.resultFlag ? response?.data?.notifications ?? [] : [];
        if (!isMounted())
            return
        if (!popoverAction.current && notifications?.length) {
            const fullNotification = notifications[0]
            let type = "success"
            switch (fullNotification.typeID) {
                case 1:
                    type = "info"
                    break;
                case 2:
                    type = "error"
                    break;
                case 3:
                    type = "success"
                    break;
                case 4:
                    type = "warning"
                    break;
                default:
                    break;
            }
            ToastService.success({
                title: fullNotification?.title,
                message: fullNotification?.body,
                props: {
                    type: type,
                    position: 'top-right',
                    theme: 'colored',
                },
            });
        }
    }, [getNotifications, isMounted])

    /**
     * As soon as the component mounts:
     * registers a listener for the new messages received from firebase that will update the internal list.
     * as soon as the component un-mounts, the listener is un-subscribed.
     */
    useEffect(() => {
        void getUnreadCount();
        if (hasNotificationPermission) {
            let unsubscribe;
            FirebaseNotificationService.onMessageReceived(onInAppNotificationReceived).then((_cb) => {
                unsubscribe = _cb;
            })
            return () => {
                if (unsubscribe) unsubscribe()
            }
        }

    }, [getUnreadCount, hasNotificationPermission, onInAppNotificationReceived])


    /**
     * Listens to the messages from the service worker and with change message sent, determines if an action is
     * required.
     * @param {MessageEvent} message
     */
    const onServiceWorkerMessageReceived = useCallback((message) => {
        switch (message.data) {
            case "openNotifications":
                if (!popoverAnchor) {
                    openPopover()
                }
                break;
            default:
                break;
        }
    }, [openPopover, popoverAnchor])

    /**
     * As soon as the component mounts:
     * sets a global callback for opening the notifications button
     */
    useEffect(() => {
        window.partner = {
            ...(window.partner ?? {}),
            openNotifications: () => openPopover(),
            notificationOpen: () => !!popoverAnchor,
        }
        if ('serviceWorker' in navigator)
            navigator.serviceWorker.addEventListener('message', onServiceWorkerMessageReceived)
        return () => {
            window.partner = {
                ...(window.partner ?? {}),
                openNotifications: undefined,
                notificationOpen: undefined,
            }
            if ('serviceWorker' in navigator)
                navigator.serviceWorker?.removeEventListener('message', onServiceWorkerMessageReceived)
        }
    }, [onServiceWorkerMessageReceived, openPopover, popoverAnchor])


    /**
     * With each change in the [passedBreakpoint] value:
     * - Close the popover
     */
    useEffect(() => {
        setPopoverAnchor(undefined)
    }, [passedBreakpoint])

    /**
     * Update the popover position on window resize.
     */
    useEffect(() => {
        if (!popoverAnchor)
            return;
        const debounced = debounce(() => {
            popoverAction.current?.updatePosition()
        }, 1000)
        window.addEventListener('resize', debounced);
        return () => window.removeEventListener('resize', debounced);
    }, [popoverAnchor])


    const popover = useMemo(() => (
        <Popover
            id={'notifications-popover'}
            action={popoverAction}
            anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'center',
            }}
            transformOrigin={{
                vertical: 'top',
                horizontal: 'center',
            }}
            slotProps={{root: {sx: {marginBlockStart: 2}}, paper: {sx: (theme) => ({boxShadow: theme.shadows[2]})}}}
            open={!!popoverAnchor}
            anchorEl={popoverAnchor}
            onClose={closePopover}
        >
            <NotificationsContent
                hasAccess={hasAccess}
                onClose={closePopover}
                hasPermission={hasNotificationPermission}
                getNotifications={getNotifications}
                unAuthorizedOrAccessDeniedCallback={unAuthorizedOrAccessDeniedCallback}
                setUnreadCount={setUnreadCount}
            />
        </Popover>
    ), [popoverAnchor, closePopover, hasAccess, hasNotificationPermission, getNotifications, unAuthorizedOrAccessDeniedCallback]);

    return useMemo(() =>
            (!hasAccess || !operatorAccessLevels.packages.notifications.viewList)
                ? null
                : ({
                    key: "notifications",
                    title: t('title'),
                    icon:
                        <Badge max={99} badgeContent={unreadCount} color={'error'}>
                            {
                                popoverAnchor
                                    ? <NotificationsRounded/>
                                    : <NotificationsOutlined/>
                            }
                        </Badge>,
                    color: "primary",
                    onClick: openPopover,
                    children: popover,
                    id: 'panel-notifications-popover-button'
                }),
        [hasAccess, operatorAccessLevels.packages.notifications.viewList, t, unreadCount, popoverAnchor, openPopover, popover])
}


const initialPaginationInfo = {
    currentPage: 1,
    pageSize: 5,
    length: 0,
};

const NotificationsContent = ({
                                  hasAccess,
                                  onClose,
                                  hasPermission,
                                  getNotifications: getNotificationsProps,
                                  unAuthorizedOrAccessDeniedCallback,
                                  setUnreadCount
                              }) => {
    const {t} = useTranslation("components", {keyPrefix: 'panel-sections.app-bar.actions.notifications'});
    const operatorAccessLevels = useAccessLevels();
    const history = useHistory();
    const [loading, setLoading] = useState(true);
    const [paginationInfo, setPaginationInfo] = useState(initialPaginationInfo);
    const [notifications, setNotifications] = useState([]);
    const [readingAll, setReadingAll] = useState(false)
    const isMounted = useIsMounted();
    const containerRef = useRef();

    /**
     * Fetches the notifications of this user from the server provided the current pagination info.
     *
     * this method is called with a pagination info of pageSize 1 and currentPage 1 if and only if a new item has
     * been received from the notifications.
     * @param {any} paginationInfo the paginationInfo of the search.
     */
    const getNotifications = useCallback(async (paginationInfo) => {
        setLoading(true);
        const response = await getNotificationsProps(paginationInfo);
        if (!isMounted())
            return;
        setLoading(false);
        if (response?.resultFlag) {
            setPaginationInfo(prevState => ({...prevState, length: response?.data?.total ?? 0}))
            if (paginationInfo.pageSize === 1) {
                setNotifications(() => [
                    ...(response?.data?.notifications ?? []),
                ])
            } else {
                setNotifications(prevState => [
                    ...prevState,
                    ...(response?.data?.notifications ?? [])
                ])
            }
        }
    }, [getNotificationsProps, isMounted])

    /**
     * As soon as the component mounts:
     * - fetches the notifications of the panel.
     */
    useEffect(() => {
        void getNotifications(initialPaginationInfo);
    }, [getNotifications])

    /**
     * Navigates the user to notification management view
     */
    const navigateToNotificationManagementView = useCallback(() => {
        history.push(routes.notificationManagement);
        onClose();
    }, [onClose, history])

    /**
     * Reads all the notifications of the system that are currently un-read from the server..
     */
    const readAllNotifications = useCallback(async () => {
        if (!operatorAccessLevels.packages.notifications.viewList) return;
        setReadingAll(true);
        const response = await Api.markAllNotificationAsRead(unAuthorizedOrAccessDeniedCallback);
        if (!isMounted())
            return;
        setReadingAll(false);
        if (response?.resultFlag) {
            setNotifications(prevState => prevState.map(e => ({...e, isRead: true})))
            setUnreadCount(0)
        }
    }, [isMounted, operatorAccessLevels.packages.notifications.viewList, setUnreadCount, unAuthorizedOrAccessDeniedCallback])

    /**
     * Loads more parts by increasing the currentPage of pagination info
     */
    const loadMore = useCallback(() => {
        if (loading)
            return;
        setLoading(true)
        const _paginationInfo = {...paginationInfo, currentPage: paginationInfo.currentPage + 1}
        setPaginationInfo(_paginationInfo)
        void getNotifications(_paginationInfo);
    }, [loading, paginationInfo, getNotifications])

    /**
     * Saves the updated version of a notification in the list of contained notifications.
     *
     * @param {any} notification the notification to be synced in the list.
     * @param {boolean} toggledReadStatus whether the read status is toggled.
     */
    const syncNotification = useCallback((notification, toggledReadStatus = false) => {
        setNotifications(prevState => prevState.map(e => e.id === notification.id ? notification : e))
        if (toggledReadStatus) {
            setUnreadCount(prevState => Math.max(prevState + (notification.isRead ? -1 : 1), 0))
        }
    }, [setUnreadCount])

    return (
        <>
            <div className='container-fluid notifications-container no-gutters pb-1 pr-0 pl-0'>
                <div className='justify-content-between d-flex align-items-center px-2 pt-2'>
                    <p className='title'>
                        {t('title')}
                    </p>
                    <div className={'d-flex justify-content-end align-items-center'}>
                        {
                            operatorAccessLevels.packages.notifications.manageList &&
                            <IconButton
                                disabled={!hasAccess}
                                onPressed={navigateToNotificationManagementView}
                                color={'secondary'}
                            >
                                <SvgIcon component={SettingsIcon} className={'icon normal'}/>
                            </IconButton>
                        }
                        {
                            !hasPermission && !loading &&
                            <>
                                <Tooltip
                                    title={t('blocked')}
                                    placement={'bottom'}>
                                    <WarningRounded
                                        className={'yellow icon large'}
                                    />
                                </Tooltip>
                            </>
                        }
                    </div>

                </div>
                {
                    !!hasAccess && notifications.length > 0 &&
                    <div className={'justify-content-between d-flex align-items-center px-2 pt-2'}>
                        <p className='text-xs text-color-3 font-weight-semi-bold'>
                            {t('latest')}
                        </p>
                        <Button
                            variant={'text'}
                            color={'primary'}
                            onClick={readAllNotifications}
                            disabled={readingAll}
                        >
                            {readingAll ? t('readingAll') : t('readAll')}
                        </Button>
                    </div>
                }
                <div className={'divider'}/>
                <div ref={containerRef} className='d-flex flex-column scroller'>
                    {
                        !hasAccess &&
                        <div className={'m-3'}>
                            <p className={'text-sm text-center text-color-3 mb-3'}>
                                {t('accessDenied')}
                            </p>
                            <AccessDenied className={'svg'}/>
                        </div>
                    }
                    {
                        !!hasAccess &&
                        <>
                            <InfiniteScroll
                                pageStart={0}
                                loadMore={loadMore}
                                hasMore={!loading && paginationInfo.length > paginationInfo.pageSize * paginationInfo.currentPage}
                                getScrollParent={() => containerRef.current}
                                useWindow={false}
                            >
                                <TransitionGroup className='d-flex flex-column'>
                                    {
                                        notifications.map((notification) =>
                                            <CSSTransition classNames='item' key={notification.id} timeout={500}>
                                                <NotificationCard
                                                    notification={notification}
                                                    syncNotification={syncNotification}
                                                    unAuthorizedOrAccessDeniedCallback={unAuthorizedOrAccessDeniedCallback}
                                                />
                                            </CSSTransition>
                                        )
                                    }
                                </TransitionGroup>
                            </InfiniteScroll>
                            {
                                loading &&
                                <Col xs={12} className={'p-0 my-4 d-flex justify-content-center'}>
                                    <CircularProgress size={30} color={'secondary'}/>
                                </Col>
                            }
                            {
                                !loading && !notifications?.length &&
                                <Col xs={12}
                                     className={'p-0 d-flex flex-column justify-content-center align-items-center'}>
                                    <NoNotifications className={'no-notifications'}/>
                                </Col>
                            }
                        </>
                    }
                </div>
            </div>
        </>
    );
}

export default useNotificationsEndAction;
